import { useEffect, useRef, useState, useCallback } from "react";
import api, { errText } from "@/lib/api";

const SNAP_W = 480;

/**
 * Camera gate + snapshot capture. The browser cannot stop a candidate physically covering or
 * disabling a camera, so this detects loss (track ended/muted, permission revoked, device
 * unplugged), blocks the assessment while the camera is down, and logs every interruption.
 */
export default function ProctorCam({ token, active, snapshots = 5, onStatus }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const takenRef = useRef(0);
  const [status, setStatus] = useState("idle"); // idle | starting | live | lost | denied | missing
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(0);

  const report = useCallback(
    (code, detail) => {
      api.post(`/assess/${token}/proctor-event`, { code, detail: detail || null }).catch(() => {});
    },
    [token],
  );

  const set = useCallback(
    (s, msg) => {
      setStatus(s);
      setMessage(msg || "");
      if (onStatus) onStatus(s);
    },
    [onStatus],
  );

  const start = useCallback(async () => {
    set("starting");
    if (!navigator.mediaDevices?.getUserMedia) {
      set("missing", "This browser cannot access a camera. Use Chrome or Firefox on a device with a camera.");
      report("camera_missing", "getUserMedia unavailable");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      const track = stream.getVideoTracks()[0];
      track.addEventListener("ended", () => {
        set("lost", "The camera stopped. Reconnect it to carry on.");
        report("camera_lost", "track ended");
      });
      track.addEventListener("mute", () => {
        set("lost", "The camera feed is blocked. Uncover it to carry on.");
        report("camera_lost", "track muted");
      });
      track.addEventListener("unmute", () => {
        set("live");
        report("camera_restored");
      });
      set("live");
      report("camera_granted");
    } catch (e) {
      const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
      if (denied) {
        set("denied", "Camera permission was refused. Allow the camera in your browser and try again.");
        report("camera_denied", e?.name);
      } else {
        set("missing", "No working camera was found on this device.");
        report("camera_missing", e?.name || "unknown");
      }
    }
  }, [report, set]);

  useEffect(() => {
    start();
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [start]);

  // Keep watching: a stopped/absent track while the assessment is running is an interruption.
  useEffect(() => {
    const t = setInterval(() => {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (status === "live" && (!track || track.readyState === "ended")) {
        set("lost", "The camera stopped. Reconnect it to carry on.");
        report("camera_lost", "track gone");
      }
    }, 3000);
    return () => clearInterval(t);
  }, [status, report, set]);

  useEffect(() => {
    const onHide = () => {
      if (document.hidden && active) report("page_hidden");
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [active, report]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || status !== "live" || takenRef.current >= snapshots) return;
    const canvas = canvasRef.current;
    const ratio = (video.videoHeight || 480) / (video.videoWidth || 640);
    canvas.width = SNAP_W;
    canvas.height = Math.round(SNAP_W * ratio);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
    try {
      const { data } = await api.post(`/assess/${token}/snapshot`, {
        image: dataUrl,
        sequence: takenRef.current,
      });
      takenRef.current += 1;
      setCount(data.count);
    } catch (err) {
      // A failed snapshot must never block the candidate, but it must not be silent either.
      api
        .post(`/assess/${token}/proctor-event`, {
          code: "camera_lost",
          detail: `snapshot upload failed: ${errText(err)}`.slice(0, 300),
        })
        .catch(() => {});
    }
  }, [status, snapshots, token]);

  // Randomised capture moments across the session, first one shortly after the start.
  useEffect(() => {
    if (!active || status !== "live") return;
    const delay = takenRef.current === 0 ? 8000 : 90000 + Math.random() * 150000;
    const t = setTimeout(capture, delay);
    return () => clearTimeout(t);
  }, [active, status, count, capture]);

  const blocked = status === "lost" || status === "denied" || status === "missing";

  return (
    <>
      <canvas ref={canvasRef} className="hidden" />
      <div
        className="fixed bottom-4 right-4 z-30 cand-card rounded-lg overflow-hidden shadow-lg"
        style={{ width: 168 }}
        data-testid="proctor-camera-panel"
      >
        <video
          ref={videoRef}
          muted
          playsInline
          className="w-full h-[112px] object-cover bg-[#241A16]"
          data-testid="proctor-video"
        />
        <div className="px-2 py-1.5 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[10px] font-data uppercase tracking-wider">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: status === "live" ? "#047857" : "#B91C1C" }}
            />
            <span data-testid="proctor-status">{status === "live" ? "camera on" : status}</span>
          </span>
          <span className="text-[10px] font-data text-[color:var(--cand-muted)]" data-testid="proctor-snapshot-count">
            {count}/{snapshots}
          </span>
        </div>
      </div>

      {blocked && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-4"
          style={{ background: "rgba(36,26,22,0.88)" }}
          data-testid="proctor-blocked-overlay"
        >
          <div className="cand-card rounded-lg p-6 sm:p-8 max-w-md">
            <h2 className="font-serif-display text-2xl">Assessment paused</h2>
            <p className="text-sm text-[color:var(--cand-muted)] mt-3 leading-relaxed" data-testid="proctor-blocked-message">
              {message}
            </p>
            <p className="text-xs text-[color:var(--cand-muted)] mt-3">
              Your answers are saved and the clock keeps running on our server, so reconnect as soon
              as you can. This interruption is recorded for the reviewer.
            </p>
            <button
              className="btn-accent rounded px-5 py-2.5 text-sm mt-5"
              data-testid="proctor-retry-button"
              onClick={start}
            >
              Try the camera again
            </button>
          </div>
        </div>
      )}
    </>
  );
}
